import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';

// routes
import { PAGE } from '@/routes';
import { useLocation, useNavigate, useBlocker } from 'react-router-dom';

// services
import { budgetService } from '@/services/budget.service';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { Budget } from '@/services/types/budgets.types';
import { accountingCenterService } from '@/services/accounting-centers.service';
import { bankAccountService } from '@/services/bank-accounts.service';
import { expenseRevenueService } from '@/services/expense-category.service';
import { revenueCategoryService } from '@/services/revenue-category.service';

// utils
import { formatNumberToK } from '@/utils/helpers';

// icons
import { ReactComponent as InfoIcon } from '@/assets/icons/info.svg';
import { CircleXIcon, FileText, Loader2 } from 'lucide-react';
import { ReactComponent as CompleteIcon } from '@/assets/icons/completed.svg';
import { ReactComponent as ExitOutlineIcon } from '@/assets/icons/exit-outline.svg';
import { ReactComponent as SuccessIcon } from '@/assets/icons/success.svg';
import { ReactComponent as BudgetEmptyIcon } from '@/assets/icons/budget-empty.svg';

// components
import MobileAppBar from '@/components/molecules/MobileAppBar';
import CreateInvoiceSteps from '@/components/organisms/invoice/CreateInvoiceSteps';
import CreateInvoiceStepsMobile from '@/components/organisms/invoice/CreateInvoiceStepsMobile';
import CreateInvoiceHeader from '@/components/molecules/invoice/CreateInvoiceHeader';
import CreateInvoiceFooter from '@/components/molecules/invoice/CreateInvoiceFooter';
import CreateInvoiceUpload from '../../../components/organisms/invoice/form-steps/CreateInvoiceUpload';
import CreateInvoiceAllocation from '../../../components/organisms/invoice/form-steps/CreateInvoiceAllocation';
import InvoiceUploadOverview from '@/components/molecules/invoice/InvoiceUploadOverview';
import CreateInvoiceTags from '../../../components/organisms/invoice/form-steps/CreateInvoiceTags';
import { invoiceService } from '@/services/invoice.service';
import { InvoiceCategory } from '@/services/types/enums/invoice.enum';
import { InvoiceAllocation } from '@/services/types/invoice.types';
import ConfirmModal from '@/components/molecules/ConfirmModal';
import InfoModal from '@/components/molecules/InfoModal';
import { BankAccount } from '@/services/types/bank-accounts.types';
import { AccountingCenter } from '@/services/types/accounting-centers-types';
import { ExpenseCategory } from '@/services/types/expense-category.types';
import { RevenueCategory } from '@/services/types/revenue-category.types';
import PdfThumbnail from '@/components/molecules/PdfThumbnail';

const InvoiceCreatePage = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [budgetOptions, setBudgetOptions] = useState<{ value: string; label: string }[]>([]);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const nextLocationRef = useRef<string | null>(null);
  const isSuccessfulNavigationRef = useRef<boolean>(false);
  const [showInvoiceSuccessModal, setShowInvoiceSuccessModal] = useState(false);
  const [processingInvoice, setProcessingInvoice] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [accountingCenters, setAccountingCenters] = useState<AccountingCenter[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [revenueCategories, setRevenueCategories] = useState<RevenueCategory[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [paymentTermType, setPaymentTermType] = useState<'fixed' | 'window'>('fixed');
  const [loadingBudgets, setLoadingBudgets] = useState(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { from, budgetId = '', invoiceId = '', issueDate = '' } = location.state || {};

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    control,
    formState: { errors }
  } = useForm<any>({
    defaultValues: {
      invoice_category: 'expense',
      payment_type: from === 'commitment' ? 'NOT_PAID' : 'ALREADY_PAID',
      bank_account: [],
      department_accounting_center: [],
      product_and_services_accounting_center: [],
      project_accounting_center: [],
      contract_accounting_center: [],
      invoice_categories: [],
      budget_id: budgetId || ''
    }
  });

  const [loadingDraft, setLoadingDraft] = useState(false);

  // Narrowly-scoped reactive subscriptions. Avoid `watch()` (no args) which would
  // re-render the whole page on every keystroke in any field.
  const budgetIdForm = useWatch({ control, name: 'budget_id' });
  const invoiceCategory = useWatch({ control, name: 'invoice_category' });
  const paymentType = useWatch({ control, name: 'payment_type' });

  // Calculate max amount based on invoice category and budget
  const maxAmount = useMemo(() => {
    if (!selectedBudget) return 0;

    if (invoiceCategory === 'expense') {
      return selectedBudget.bmev;
    } else if (invoiceCategory === 'revenue') {
      // Handle edge case: btrt_achieved can be greater than btrt
      const remaining = selectedBudget.btrt - (selectedBudget.btrt_achieved || 0);
      return Math.max(0, remaining);
    }
    return 0;
  }, [selectedBudget, invoiceCategory]);

  // Check if amount field should be disabled
  const isAmountDisabled = !budgetIdForm;

  const invoiceCategoriesData = invoiceCategory === 'expense' ? expenseCategories : revenueCategories;

  useEffect(() => {
    if (paymentType === 'NOT_PAID') {
      setValue('payment_date', null);
    }
  }, [paymentType]);

  useEffect(() => {
    if (from === 'commitment' && issueDate) {
      setValue('payment_issue_date', new Date(issueDate));
    }
  }, [from, issueDate, setValue]);

  useEffect(() => {
    setLoadingBudgets(true);
    budgetService
      .getWritableBudgets()
      .then(({ data: budgets }) => {
        const formattedBudgets = budgets.map((budget: Budget) => ({
          value: budget._id || '',
          label: budget.name
        }));
        setBudgetOptions(formattedBudgets);
      })
      .catch((error) => {
        toast.error('Error fetching budgets');
      })
      .finally(() => {
        setLoadingBudgets(false);
      });
  }, []);

  // Fetch and pre-fill draft invoice data or edit invoice data
  useEffect(() => {
    if ((from === 'draft' || from === 'invoiceDetail') && invoiceId) {
      setLoadingDraft(true);
      invoiceService
        .getInvoice(invoiceId)
        .then(async (invoice) => {
          // Set basic invoice fields
          if (invoice?.name) {
            setValue('name', invoice.name);
          }
          if (invoice?.budget_id) {
            setValue('budget_id', invoice.budget_id);
          }
          if (invoice?.invoicing_company_name) {
            setValue('invoicing_company_name', invoice.invoicing_company_name);
          }
          if (invoice?.invoiced_company_name) {
            setValue('invoiced_company_name', invoice.invoiced_company_name);
          }
          if (invoice?.invoice_category) {
            setValue('invoice_category', invoice.invoice_category.toLowerCase());
          }
          if (invoice?.payment_type) {
            setValue('payment_type', invoice.payment_type);
          }
          if (invoice?.payment_currency) {
            setValue('payment_currency', invoice.payment_currency);
          }
          if (invoice?.payment_amount) {
            setValue('payment_amount', invoice.payment_amount);
          }
          if (invoice?.payment_issue_date) {
            setValue('payment_issue_date', new Date(invoice.payment_issue_date));
          }
          if (invoice?.payment_term_date) {
            setValue('payment_term_date', new Date(invoice.payment_term_date));
          }
          if (invoice?.payment_date) {
            setValue('payment_date', new Date(invoice.payment_date));
          }
          if (invoice?.tags) {
            setValue('tags', invoice.tags || []);
          }
          if (invoice?.invoicing_contact) {
            setValue('invoicing_contact', invoice.invoicing_contact);
          }

          // Set bank account allocations
          if (invoice.allocations?.allocation_bank_account && invoice.allocations.allocation_bank_account.length > 0) {
            const bankAccountIds = invoice.allocations.allocation_bank_account.map((alloc) => alloc.id);
            setValue('bank_account', bankAccountIds);

            // Set individual allocation amounts
            invoice.allocations.allocation_bank_account.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Set department accounting center allocations
          if (
            invoice.allocations?.allocation_department_ac &&
            invoice.allocations.allocation_department_ac.length > 0
          ) {
            const deptIds = invoice.allocations.allocation_department_ac.map((alloc) => alloc.id);
            setValue('department_accounting_center', deptIds);

            invoice.allocations.allocation_department_ac.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Set products and services accounting center allocations
          if (
            invoice.allocations?.allocation_productAndServices_ac &&
            invoice.allocations.allocation_productAndServices_ac.length > 0
          ) {
            const psIds = invoice.allocations.allocation_productAndServices_ac.map((alloc) => alloc.id);
            setValue('product_and_services_accounting_center', psIds);

            invoice.allocations.allocation_productAndServices_ac.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Set project accounting center allocations
          if (invoice.allocations?.allocation_project_ac && invoice.allocations.allocation_project_ac.length > 0) {
            const projectIds = invoice.allocations.allocation_project_ac.map((alloc) => alloc.id);
            setValue('project_accounting_center', projectIds);

            invoice.allocations.allocation_project_ac.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Set contract accounting center allocations
          if (invoice.allocations?.allocation_contract_ac && invoice.allocations.allocation_contract_ac.length > 0) {
            const contractIds = invoice.allocations.allocation_contract_ac.map((alloc) => alloc.id);
            setValue('contract_accounting_center', contractIds);

            invoice.allocations.allocation_contract_ac.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Set expense category allocations
          if (
            invoice.invoice_category === InvoiceCategory.EXPENSE &&
            invoice.allocations?.allocation_expense_category &&
            invoice.allocations.allocation_expense_category.length > 0
          ) {
            const expenseIds = invoice.allocations.allocation_expense_category.map((alloc) => alloc.id);
            setValue('invoice_categories', expenseIds);

            invoice.allocations.allocation_expense_category.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Set revenue category allocations
          if (
            invoice.invoice_category === InvoiceCategory.REVENUE &&
            invoice.allocations?.allocation_revenue_category &&
            invoice.allocations.allocation_revenue_category.length > 0
          ) {
            const revenueIds = invoice.allocations.allocation_revenue_category.map((alloc) => alloc.id);
            setValue('invoice_categories', revenueIds);

            invoice.allocations.allocation_revenue_category.forEach((alloc) => {
              setValue(alloc.id, alloc.value);
            });
          }

          // Handle invoice images - store existing URLs to avoid CORS issues
          if (
            invoice.invoice_images_url &&
            invoice.invoice_images_url.length > 0 &&
            invoice.invoice_images_url.length < 10
          ) {
            setExistingImageUrls(invoice.invoice_images_url);
          }

          setLoadingDraft(false);
          toast.success(`${from === 'draft' ? 'Draft invoice' : 'Invoice'} loaded successfully`);
        })
        .catch((error) => {
          console.error(`Error fetching ${from === 'draft' ? 'draft ' : ''}invoice:`, error);
          toast.error(`Failed to load ${from === 'draft' ? 'draft ' : ''}invoice`);
          setLoadingDraft(false);
        });
    }
  }, [from, invoiceId, setValue]);

  // Fetch budget details when budgetIdForm changes
  useEffect(() => {
    if (budgetIdForm && !loadingDraft) {
      budgetService
        .getBudget(budgetIdForm)
        .then(({ data: budget }) => {
          setSelectedBudget(budget);
        })
        .catch((error) => {
          console.error('Error fetching budget:', error);
          setSelectedBudget(null);
        });
    } else {
      setSelectedBudget(null);
    }
  }, [budgetIdForm, loadingDraft]);

  useEffect(() => {
    if (budgetIdForm && !loadingDraft) {
      // Only clear fields if not loading a draft or editing an invoice
      if (from !== 'draft' && from !== 'invoiceDetail') {
        setValue('bank_account', []);
        setValue('department_accounting_center', []);
        setValue('product_and_services_accounting_center', []);
        setValue('project_accounting_center', []);
        setValue('contract_accounting_center', []);
        setValue('payment_amount', null);
        setValue('invoice_categories', []);
      }

      accountingCenterService
        .getAccountingCenters(budgetIdForm)
        .then(({ data: accountingCenters }) => {
          setAccountingCenters(accountingCenters);
        })
        .catch((error) => {
          console.error('Error fetching accounting centers:', error);
          setAccountingCenters([]);
        });

      bankAccountService
        .getBankAccounts(budgetIdForm)
        .then(({ data: bankAccounts }) => {
          setBankAccounts(bankAccounts);
        })
        .catch((error) => {
          console.error('Error fetching bank accounts:', error);
          setBankAccounts([]);
        });

      // if (invoiceCategory === 'expense') {
      expenseRevenueService
        .getExpenseCategories(budgetIdForm)
        .then(({ data: expenseCategories }) => {
          setExpenseCategories(expenseCategories);
        })
        .catch((error) => {
          console.error('Error fetching expense categories:', error);
          setExpenseCategories([]);
        });
      // } else {
      revenueCategoryService
        .getRevenueCategories(budgetIdForm)
        .then(({ data: revenueCategories }) => {
          setRevenueCategories(revenueCategories);
        })
        .catch((error) => {
          console.error('Error fetching revenue categories:', error);
          setRevenueCategories([]);
        });
      // }
    }
  }, [budgetIdForm]);

  useEffect(() => {
    setValue('invoice_categories', []);
  }, [invoiceCategory]);

  const handleCreateDraftInvoice = async () => {
    const formValues = getValues();
    const fd = new FormData();

    // Only append fields that have values
    if (formValues.name) fd.append('name', String(formValues.name));
    if (formValues.invoiced_company_name) fd.append('invoiced_company_name', formValues.invoiced_company_name);
    if (formValues.invoicing_company_name) fd.append('invoicing_company_name', formValues.invoicing_company_name);
    if (formValues.invoice_category) fd.append('invoice_category', String(formValues.invoice_category).toUpperCase());
    if (formValues.budget_id) fd.append('budget_id', formValues.budget_id);
    if (formValues.payment_term_date) fd.append('payment_term_date', formValues.payment_term_date.toISOString());
    if (formValues.payment_issue_date) fd.append('payment_issue_date', formValues.payment_issue_date.toISOString());
    if (formValues.payment_date)
      fd.append('payment_date', formValues.payment_date ? formValues.payment_date.toISOString() : null);
    if (formValues.payment_currency) fd.append('payment_currency', formValues.payment_currency);
    if (formValues.payment_type) fd.append('payment_type', formValues.payment_type);
    if (formValues.payment_amount) fd.append('payment_amount', formValues.payment_amount);
    if (formValues.tags && formValues.tags.length > 0) fd.append('tags', JSON.stringify(formValues.tags));
    if (formValues.invoicing_contact) fd.append('invoicing_contact', formValues.invoicing_contact);
    // Append new invoice images if any
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((file) => {
        fd.append('invoice_images', file);
      });
    }

    // Append existing image URLs to keep (for updating existing draft)
    if (existingImageUrls.length > 0) {
      fd.append('existing_images', JSON.stringify(existingImageUrls));
    }

    // Build allocations object only if there are allocations
    const hasAllocations =
      (formValues.department_accounting_center && formValues.department_accounting_center.length > 0) ||
      (formValues.product_and_services_accounting_center &&
        formValues.product_and_services_accounting_center.length > 0) ||
      (formValues.project_accounting_center && formValues.project_accounting_center.length > 0) ||
      (formValues.contract_accounting_center && formValues.contract_accounting_center.length > 0) ||
      (formValues.bank_account && formValues.bank_account.length > 0) ||
      (formValues.invoice_categories && formValues.invoice_categories.length > 0);

    if (hasAllocations) {
      const allocations: InvoiceAllocation = {};

      // Department AC allocations
      if (formValues.department_accounting_center && formValues.department_accounting_center.length > 0) {
        allocations.allocation_department_ac = formValues.department_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Products & Services AC allocations
      if (
        formValues.product_and_services_accounting_center &&
        formValues.product_and_services_accounting_center.length > 0
      ) {
        allocations.allocation_productAndServices_ac = formValues.product_and_services_accounting_center.map(
          (item: any) => ({
            id: item,
            value: Number(formValues[`${item}`]) || 0
          })
        );
      }

      // Project AC allocations
      if (formValues.project_accounting_center && formValues.project_accounting_center.length > 0) {
        allocations.allocation_project_ac = formValues.project_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Contract AC allocations
      if (formValues.contract_accounting_center && formValues.contract_accounting_center.length > 0) {
        allocations.allocation_contract_ac = formValues.contract_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Bank Account allocations
      if (formValues.bank_account && formValues.bank_account.length > 0) {
        allocations.allocation_bank_account = formValues.bank_account.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Expense/Revenue Category allocations
      if (formValues.invoice_categories && formValues.invoice_categories.length > 0) {
        if (formValues.invoice_category === 'expense') {
          allocations.allocation_expense_category = formValues.invoice_categories.map((item: any) => ({
            id: item,
            value: Number(formValues[`${item}`]) || 0
          }));
        } else if (formValues.invoice_category === 'revenue') {
          allocations.allocation_revenue_category = formValues.invoice_categories.map((item: any) => ({
            id: item,
            value: Number(formValues[`${item}`]) || 0
          }));
        }
      }

      fd.append('allocations', JSON.stringify(allocations));
    }

    // Mark as draft
    fd.append('is_draft', 'true');

    try {
      let response;
      if (from === 'draft' && invoiceId) {
        // Update existing draft invoice
        response = await invoiceService.updateDraftInvoice(invoiceId, fd);
        toast.success('Draft invoice updated successfully');
        // Set flag to bypass blocker for successful navigation
        isSuccessfulNavigationRef.current = true;
        navigate(PAGE.INVOICE.path);
        // Reset flag after a short delay to ensure navigation completes
        setTimeout(() => {
          isSuccessfulNavigationRef.current = false;
        }, 100);
      } else {
        // Create new draft invoice
        response = await invoiceService.createDraftInvoice(fd);
        toast.success('Draft invoice saved successfully');
        // Set flag to bypass blocker for successful navigation
        isSuccessfulNavigationRef.current = true;
        navigate(PAGE.INVOICE.path);
        // Reset flag after a short delay to ensure navigation completes
        setTimeout(() => {
          isSuccessfulNavigationRef.current = false;
        }, 100);
      }
    } catch (error) {
      console.error('Error saving draft invoice:', error);
      toast.error(`Failed to ${from === 'draft' ? 'update' : 'save'} draft invoice`);
    }
  };

  // Subscribe only to the specific fields that determine "has form data" — avoids
  // rerendering on every keystroke for fields that don't affect this flag.
  const hasFormDataValues = useWatch({
    control,
    name: [
      'name',
      'invoicing_company_name',
      'invoiced_company_name',
      'payment_currency',
      'payment_amount',
      'payment_issue_date',
      'payment_term_date',
      'payment_date',
      'tags',
      'bank_account',
      'department_accounting_center',
      'product_and_services_accounting_center',
      'project_accounting_center',
      'contract_accounting_center',
      'invoice_categories'
    ]
  });

  const hasFormData = useMemo(() => {
    const [
      name,
      invoicingCompanyName,
      invoicedCompanyName,
      paymentCurrency,
      paymentAmount,
      paymentIssueDate,
      paymentTermDate,
      paymentDate,
      tags,
      bankAccount,
      departmentAC,
      productServicesAC,
      projectAC,
      contractAC,
      invoiceCategories
    ] = hasFormDataValues || [];

    const hasBasicFields = !!(
      (name && typeof name === 'string' && name.trim() !== '') ||
      (invoicingCompanyName && typeof invoicingCompanyName === 'string' && invoicingCompanyName.trim() !== '') ||
      (invoicedCompanyName && typeof invoicedCompanyName === 'string' && invoicedCompanyName.trim() !== '') ||
      paymentCurrency ||
      (paymentAmount && Number(paymentAmount) > 0) ||
      paymentIssueDate ||
      paymentTermDate ||
      paymentDate ||
      (tags && Array.isArray(tags) && tags.length > 0)
    );

    const hasFiles = uploadedFiles.length > 0 || existingImageUrls.length > 0;

    const hasAllocations = !!(
      (bankAccount && bankAccount.length > 0) ||
      (departmentAC && departmentAC.length > 0) ||
      (productServicesAC && productServicesAC.length > 0) ||
      (projectAC && projectAC.length > 0) ||
      (contractAC && contractAC.length > 0) ||
      (invoiceCategories && invoiceCategories.length > 0)
    );

    return hasBasicFields || hasFiles || hasAllocations;
  }, [hasFormDataValues, uploadedFiles, existingImageUrls]);

  // Block navigation when form has data (but not if it's a successful completion navigation or editing invoice)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if this is a successful navigation after invoice creation
    if (isSuccessfulNavigationRef.current) {
      return false;
    }
    // Don't block if editing an invoice (from === 'invoiceDetail')
    if (from === 'invoiceDetail') {
      return false;
    }
    const shouldBlock = hasFormData && currentLocation.pathname !== nextLocation.pathname;
    if (shouldBlock) {
      // Store the pending navigation path in ref (don't set state in callback)
      nextLocationRef.current = nextLocation.pathname;
    }
    return shouldBlock;
  });

  const hasFirstStepErrors = useMemo(() => {
    return (
      errors.name ||
      errors.budget_id ||
      errors.invoicing_company_name ||
      errors.invoiced_company_name ||
      errors.invoice_category ||
      errors.payment_type ||
      errors.payment_currency ||
      errors.payment_amount ||
      errors.payment_issue_date ||
      errors.payment_term_date ||
      errors.payment_date ||
      errors.tags
    );
  }, [errors]);

  // Handle blocked navigation - show modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked' && hasFormData && nextLocationRef.current) {
      // Store the pending navigation path
      setPendingNavigation(nextLocationRef.current);
      // Show the exit modal
      setActiveModal('exitInvoiceCreation');
    }
  }, [blocker.state, hasFormData]);

  const handlePrevClick = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    } else {
      // Only show modal if form has data
      if (hasFormData) {
        setActiveModal('exitInvoiceCreation');
      } else {
        // No data, just navigate back
        navigate(-1);
      }
    }
  };

  const handleNextClick = async () => {
    if (activeStep === 1) {
      // Validate step 1 fields
      const formData = watch();
      const requiredFields = [
        { name: 'name', label: 'Invoice Name' },
        { name: 'budget_id', label: 'Budget' },
        { name: 'invoicing_company_name', label: 'Invoicing company / person name' },
        { name: 'invoiced_company_name', label: 'Invoiced company / person name' },
        { name: 'invoice_category', label: 'Invoice Type' },
        { name: 'payment_type', label: 'Payment information' },
        { name: 'payment_currency', label: 'Currency' },
        { name: 'payment_amount', label: 'Amount' },
        { name: 'payment_issue_date', label: 'Issue date' },
        { name: 'payment_term_date', label: 'Payment term' },
        { name: 'invoicing_contact', label: 'Invoicing contact' }
        // { name: 'payment_date', label: 'Payment date' }
      ];

      let hasErrors = false;

      // Check if files are uploaded
      if ((!uploadedFiles || uploadedFiles.length === 0) && existingImageUrls.length === 0) {
        toast.error('Please upload at least one invoice file');
        hasErrors = true;
      }

      // Validate required fields
      requiredFields.forEach((field) => {
        const value = formData[field.name];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          setError(field.name, {
            type: 'manual',
            message: `${field.label} is required`
          });
          hasErrors = true;
        }
      });

      if (hasErrors) {
        toast.error('Please fill in all required fields');
        return;
      }

      // if (formData.payment_amount > maxAmount) {
      //   toast.error(`Amount cannot exceed the maximum amount of €${maxAmount}`);
      //   return;
      // }

      // Check for missing required entities
      const missingEntities: string[] = [];

      if (bankAccounts.length === 0) {
        missingEntities.push('Bank Account');
      }

      // Check if at least one accounting center type is available
      const hasAccountingCenter =
        accountingCenters.filter((ac) => ac.type === 'DEPARTMENT_ACCOUNTING').length > 0 ||
        accountingCenters.filter((ac) => ac.type === 'CONTRACTS').length > 0 ||
        accountingCenters.filter((ac) => ac.type === 'PROJECTS').length > 0 ||
        accountingCenters.filter((ac) => ac.type === 'PRODUCTS_AND_SERVICES').length > 0;

      if (!hasAccountingCenter) {
        missingEntities.push('At least one Accounting Center needs to be enabled');
      }

      const invoiceCategoriesData = invoiceCategory === 'expense' ? expenseCategories : revenueCategories;

      if (invoiceCategoriesData.length === 0) {
        missingEntities.push(`${invoiceCategory === 'expense' ? 'Expense' : 'Revenue'} Category`);
      }

      if (missingEntities.length > 0) {
        toast.custom(
          (t) => (
            <div
              className={`${
                t.visible ? 'animate-enter' : 'animate-leave'
              } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
            >
              <div className="flex-1 w-0 p-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <CircleXIcon className="h-6 w-6 text-red-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-red-400 font-semibold text-gray-900">
                      {missingEntities.length === 1
                        ? 'Please create/enable at least one:'
                        : 'Please create/enable at least one of each:'}
                    </p>
                    <div className="mt-2 text-sm text-gray-700">
                      {missingEntities.map((entity, index) => (
                        <div key={index} className="flex items-start mt-1">
                          <span className="mr-2">•</span>
                          <span className="font-bold">{entity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ),
          {
            duration: 5000
          }
        );
        return;
      }
    }
    if (activeStep === 2) {
      // Validate step 2 fields - only check allocations that have been selected.
      // Compute equality on-demand from current values so we don't need a reactive
      // `isEqual` recomputed on every keystroke at the parent level.
      const formValues = getValues();
      const allocAmount = Number(formValues.payment_amount) || 0;
      const sumIds = (ids: any) => {
        if (!Array.isArray(ids)) return 0;
        return ids.reduce((sum: number, id: string) => sum + (parseFloat(formValues[id]) || 0), 0);
      };
      const isEqual = {
        bankAccounts: sumIds(formValues.bank_account) === allocAmount,
        departmentsAC: sumIds(formValues.department_accounting_center) === allocAmount,
        productsAndServicesAC: sumIds(formValues.product_and_services_accounting_center) === allocAmount,
        projectAC: sumIds(formValues.project_accounting_center) === allocAmount,
        contractAC: sumIds(formValues.contract_accounting_center) === allocAmount,
        invoiceCategory: sumIds(formValues.invoice_categories) === allocAmount
      };

      const insufficientBankAccounts = (formValues.bank_account || [])
        .map((id: string) => {
          const bank = bankAccounts.find((account) => account._id === id);
          const allocatedAmount = parseFloat(formValues[id]) || 0;
          const remainingBalance = bank?.remainingBalance ?? 0;

          if (allocatedAmount > remainingBalance) {
            return {
              name: bank?.accountName || 'Bank account',
              remainingBalance
            };
          }

          return null;
        })
        .filter(Boolean) as { name: string; remainingBalance: number }[];

      if (insufficientBankAccounts.length > 0) {
        toast.error(
          insufficientBankAccounts
            .map(
              (bank) =>
                `${bank.name} has insufficient funds (Remaining Balance €${bank.remainingBalance.toLocaleString()})`
            )
            .join('\n')
        );
        return;
      }

      const errors: string[] = [];

      if (formValues.bank_account && formValues.bank_account.length > 0 && !isEqual.bankAccounts) {
        errors.push('Bank Accounts');
      }

      if (
        accountingCenters.filter((ac) => ac.type === 'DEPARTMENT_ACCOUNTING').length > 0 &&
        formValues.department_accounting_center &&
        formValues.department_accounting_center.length > 0 &&
        !isEqual.departmentsAC
      ) {
        errors.push('Department Accounting Center');
      }

      if (
        accountingCenters.filter((ac) => ac.type === 'PRODUCTS_AND_SERVICES').length > 0 &&
        formValues.product_and_services_accounting_center &&
        formValues.product_and_services_accounting_center.length > 0 &&
        !isEqual.productsAndServicesAC
      ) {
        errors.push('Products and Services Accounting Center');
      }

      if (
        accountingCenters.filter((ac) => ac.type === 'PROJECTS').length > 0 &&
        formValues.project_accounting_center &&
        formValues.project_accounting_center.length > 0 &&
        !isEqual.projectAC
      ) {
        errors.push('Project Accounting Center');
      }

      if (
        accountingCenters.filter((ac) => ac.type === 'CONTRACTS').length > 0 &&
        formValues.contract_accounting_center &&
        formValues.contract_accounting_center.length > 0 &&
        !isEqual.contractAC
      ) {
        errors.push('Contract Accounting Center');
      }

      if (formValues.invoice_categories && formValues.invoice_categories.length > 0 && !isEqual.invoiceCategory) {
        errors.push('Invoice Categories');
      }

      if (errors.length > 0) {
        toast.error(`Invoice allocation must have a value equal to the Invoice amount (€${allocAmount})}`);
        return;
      }
    }

    if (activeStep === 3) {
      const formValues = getValues();
      const fd = new FormData();
      fd.append('name', String(formValues.name));
      fd.append('invoiced_company_name', formValues.invoiced_company_name);
      fd.append('invoicing_company_name', formValues.invoicing_company_name);
      fd.append('invoice_category', String(formValues.invoice_category).toUpperCase());
      fd.append('budget_id', formValues.budget_id);
      fd.append('payment_term_date', formValues.payment_term_date.toISOString());
      fd.append('payment_issue_date', formValues.payment_issue_date.toISOString());
      if (formValues.payment_date) {
        fd.append('payment_date', formValues.payment_date ? formValues.payment_date.toISOString() : null);
      }
      fd.append('payment_currency', formValues.payment_currency);
      fd.append('payment_type', formValues.payment_type);
      fd.append('payment_amount', formValues.payment_amount);
      fd.append('invoicing_contact', formValues.invoicing_contact);
      fd.append('tags', JSON.stringify(formValues.tags));

      // Append new invoice images (new uploads)
      uploadedFiles.forEach((file) => {
        fd.append('invoice_images', file);
      });

      // Append existing image URLs to keep (backend will handle deletion of removed images)
      if (existingImageUrls.length > 0) {
        fd.append('existing_images', JSON.stringify(existingImageUrls));
      }

      fd.append('is_draft', 'false');
      // fd.append(
      //   'allocations',
      //   JSON.stringify({
      //     allocation_accounting_center: [
      //       ...formValues.department_accounting_center.map((item: any) => {
      //         return {
      //           [`${item}`]: formValues[`${item}`]
      //         };
      //       }),
      //       // ...formValues.product_and_services_accounting_center.map((item: any) => {
      //       //   return {
      //       //     [`${item}`]: formValues[`${item}`]
      //       //   };
      //       // }),
      //       // ...formValues.project_accounting_center.map((item: any) => {
      //       //   return {
      //       //     [`${item}`]: formValues[`${item}`]
      //       //   };
      //       // }),
      //       // ...formValues.contract_accounting_center.map((item: any) => {
      //       //   return {
      //       //     [`${item}`]: formValues[`${item}`]
      //       //   };
      //       // })
      //     ],
      //   })
      // );

      // Log all values of fd (FormData)
      // Array.from(fd.entries()).forEach(([key, value]) => {
      //   console.log(`${key}:`, value);
      // });

      const allocations: InvoiceAllocation = {};

      // Only include department AC if available and selected
      if (formValues.department_accounting_center && formValues.department_accounting_center.length > 0) {
        allocations.allocation_department_ac = formValues.department_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`])
        }));
      }

      // Only include products and services AC if available and selected
      if (
        formValues.product_and_services_accounting_center &&
        formValues.product_and_services_accounting_center.length > 0
      ) {
        allocations.allocation_productAndServices_ac = formValues.product_and_services_accounting_center.map(
          (item: any) => ({
            id: item,
            value: Number(formValues[`${item}`])
          })
        );
      }

      // Only include project AC if available and selected
      if (formValues.project_accounting_center && formValues.project_accounting_center.length > 0) {
        allocations.allocation_project_ac = formValues.project_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`])
        }));
      }

      // Only include contract AC if available and selected
      if (formValues.contract_accounting_center && formValues.contract_accounting_center.length > 0) {
        allocations.allocation_contract_ac = formValues.contract_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`])
        }));
      }

      // Bank accounts
      if (formValues.bank_account && formValues.bank_account.length > 0) {
        allocations.allocation_bank_account = formValues.bank_account.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`])
        }));
      }

      // Invoice categories
      if (formValues.invoice_categories && formValues.invoice_categories.length > 0) {
        if (formValues.invoice_category === 'expense') {
          allocations.allocation_expense_category = formValues.invoice_categories.map((item: any) => ({
            id: item,
            value: Number(formValues[`${item}`])
          }));
        } else {
          allocations.allocation_revenue_category = formValues.invoice_categories.map((item: any) => ({
            id: item,
            value: Number(formValues[`${item}`])
          }));
        }
      }

      fd.append('allocations', JSON.stringify(allocations));

      // const payload = {
      //   name: formValues.name,
      //   invoiced_company_name: formValues.invoiced_company_name,
      //   invoicing_company_name: formValues.invoicing_company_name,
      //   invoice_category: formValues.invoice_category === 'expense' ? InvoiceCategory.EXPENSE : InvoiceCategory.REVENUE,
      //   budget_id: formValues.budget_id,
      //   payment_type: formValues.payment_type,
      //   payment_currency: formValues.payment_currency,
      //   payment_amount: Number(formValues.payment_amount),
      //   payment_term_date: formValues.payment_term_date,
      //   payment_issue_date: formValues.payment_issue_date,
      //   payment_date: formValues.payment_date,
      //   allocations,
      //   tags: formValues.tags
      // };

      // console.log(payload);

      setProcessingInvoice(true);
      try {
        let response;
        if (from === 'invoiceDetail' && invoiceId) {
          // Update existing draft invoice
          response = await invoiceService.updateInvoice(invoiceId, fd);
          toast.success('Invoice updated successfully');
          // Set flag to bypass blocker for successful navigation
          isSuccessfulNavigationRef.current = true;
          setShowInvoiceSuccessModal(true);
          // navigate(PAGE.INVOICE.path);
          // Reset flag after a short delay to ensure navigation completes
          setTimeout(() => {
            isSuccessfulNavigationRef.current = false;
          }, 100);
        } else {
          if (from === 'draft' && invoiceId) {
            fd.append('draft_id', invoiceId);
          }
          // Create new invoice
          response = await invoiceService.createInvoice(fd);
          toast.success('Invoice created successfully');
          // Set flag to bypass blocker for successful navigation
          isSuccessfulNavigationRef.current = true;
          setShowInvoiceSuccessModal(true);
          // navigate(PAGE.INVOICE.path);
          // Reset flag after a short delay to ensure navigation completes
          setTimeout(() => {
            isSuccessfulNavigationRef.current = false;
          }, 100);
        }
      } catch (error) {
        console.error('Error saving invoice:', error);
        toast.error(
          `Failed to ${from === 'invoiceDetail' ? 'update' : from === 'draft' ? 'update' : 'create'} invoice`
        );
      } finally {
        setProcessingInvoice(false);
      }
    }

    if (activeStep < 3) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleFilesChange = (files: File[]) => {
    setUploadedFiles(files);
  };

  const handleRemoveExistingImage = (url: string) => {
    setExistingImageUrls((prev) => prev.filter((imageUrl) => imageUrl !== url));
  };

  const onSubmit = (_data: any) => {};

  useEffect(() => {
    // Sets breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: from === 'invoice' ? 'Invoices' : 'Budgets',
          path: from === 'invoice' ? PAGE.INVOICE.path : PAGE.BUDGETS.path + '/' + budgetId
        },
        {
          title: from === 'invoiceDetail' ? 'Edit Invoice' : 'Create Invoice',
          path: ''
        }
      ])
    );
  }, [dispatch]);

  if (loadingDraft) {
    return (
      <div className="flex items-center justify-center h-full flex-col gap-2">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm font-medium">Loading draft invoice...</span>
      </div>
    );
  }

  // Save draft without navigation (used for exit flow)
  const saveDraftWithoutNavigation = async () => {
    const formValues = getValues();
    const fd = new FormData();

    // Only append fields that have values
    if (formValues.name) fd.append('name', String(formValues.name));
    if (formValues.invoiced_company_name) fd.append('invoiced_company_name', formValues.invoiced_company_name);
    if (formValues.invoicing_company_name) fd.append('invoicing_company_name', formValues.invoicing_company_name);
    if (formValues.invoice_category) fd.append('invoice_category', String(formValues.invoice_category).toUpperCase());
    if (formValues.budget_id) fd.append('budget_id', formValues.budget_id);
    if (formValues.payment_term_date) fd.append('payment_term_date', formValues.payment_term_date.toISOString());
    if (formValues.payment_issue_date) fd.append('payment_issue_date', formValues.payment_issue_date.toISOString());
    if (formValues.payment_date)
      fd.append('payment_date', formValues.payment_date ? formValues.payment_date.toISOString() : null);
    if (formValues.payment_currency) fd.append('payment_currency', formValues.payment_currency);
    if (formValues.payment_type) fd.append('payment_type', formValues.payment_type);
    if (formValues.payment_amount) fd.append('payment_amount', formValues.payment_amount);
    if (formValues.tags && formValues.tags.length > 0) fd.append('tags', JSON.stringify(formValues.tags));
    if (formValues.invoicing_contact) fd.append('invoicing_contact', formValues.invoicing_contact);
    // Append new invoice images if any
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((file) => {
        fd.append('invoice_images', file);
      });
    }

    // Append existing image URLs to keep (for updating existing draft)
    if (existingImageUrls.length > 0) {
      fd.append('existing_images', JSON.stringify(existingImageUrls));
    }

    // Build allocations object only if there are allocations
    const hasAllocations =
      (formValues.department_accounting_center && formValues.department_accounting_center.length > 0) ||
      (formValues.product_and_services_accounting_center &&
        formValues.product_and_services_accounting_center.length > 0) ||
      (formValues.project_accounting_center && formValues.project_accounting_center.length > 0) ||
      (formValues.contract_accounting_center && formValues.contract_accounting_center.length > 0) ||
      (formValues.bank_account && formValues.bank_account.length > 0) ||
      (formValues.invoice_categories && formValues.invoice_categories.length > 0);

    if (hasAllocations) {
      const allocations: InvoiceAllocation = {};

      // Department AC allocations
      if (formValues.department_accounting_center && formValues.department_accounting_center.length > 0) {
        allocations.allocation_department_ac = formValues.department_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Products & Services AC allocations
      if (
        formValues.product_and_services_accounting_center &&
        formValues.product_and_services_accounting_center.length > 0
      ) {
        allocations.allocation_productAndServices_ac = formValues.product_and_services_accounting_center.map(
          (item: any) => ({
            id: item,
            value: Number(formValues[`${item}`]) || 0
          })
        );
      }

      // Project AC allocations
      if (formValues.project_accounting_center && formValues.project_accounting_center.length > 0) {
        allocations.allocation_project_ac = formValues.project_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Contract AC allocations
      if (formValues.contract_accounting_center && formValues.contract_accounting_center.length > 0) {
        allocations.allocation_contract_ac = formValues.contract_accounting_center.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Bank Account allocations
      if (formValues.bank_account && formValues.bank_account.length > 0) {
        allocations.allocation_bank_account = formValues.bank_account.map((item: any) => ({
          id: item,
          value: Number(formValues[`${item}`]) || 0
        }));
      }

      // Expense/Revenue Category allocations
      if (formValues.invoice_categories && formValues.invoice_categories.length > 0) {
        if (formValues.invoice_category === 'expense') {
          allocations.allocation_expense_category = formValues.invoice_categories.map((item: any) => ({
            id: item,
            value: Number(formValues[`${item}`]) || 0
          }));
        } else if (formValues.invoice_category === 'revenue') {
          allocations.allocation_revenue_category = formValues.invoice_categories.map((item: any) => ({
            id: item,
            value: Number(formValues[`${item}`]) || 0
          }));
        }
      }

      fd.append('allocations', JSON.stringify(allocations));
    }

    // Mark as draft
    fd.append('is_draft', 'true');

    let response;
    if (from === 'draft' && invoiceId) {
      // Update existing draft invoice
      response = await invoiceService.updateDraftInvoice(invoiceId, fd);
      toast.success('Draft invoice updated successfully');
    } else {
      // Create new draft invoice
      response = await invoiceService.createDraftInvoice(fd);
      toast.success('Draft invoice saved successfully');
    }
    return response;
  };

  const handleExitInvoiceCreation = async () => {
    setActiveModal(null);

    // Store the pending navigation path before async operations
    const targetPath = pendingNavigation;

    // IMPORTANT: Set flag to bypass blocker BEFORE any async operations
    isSuccessfulNavigationRef.current = true;

    // Reset blocker state
    setPendingNavigation(null);
    nextLocationRef.current = null;
    if (blocker.state === 'blocked') {
      blocker.reset();
    }

    try {
      // Determine target navigation path
      let navigationPath = targetPath;
      if (!navigationPath) {
        if (from === 'invoice') {
          navigationPath = PAGE.INVOICE.path;
        } else if (budgetId) {
          navigationPath = `${PAGE.BUDGETS.path}/${budgetId}`;
        }
      }

      // Save draft before exiting (without navigation)
      await saveDraftWithoutNavigation();

      // Navigate after draft is saved
      if (navigationPath) {
        navigate(navigationPath);
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error saving draft on exit:', error);
      // Still navigate even if save fails
      let navigationPath = targetPath;
      if (!navigationPath) {
        if (from === 'invoice') {
          navigationPath = PAGE.INVOICE.path;
        } else if (budgetId) {
          navigationPath = `${PAGE.BUDGETS.path}/${budgetId}`;
        }
      }

      if (navigationPath) {
        navigate(navigationPath);
      } else {
        navigate(-1);
      }
    } finally {
      // Reset flag after navigation completes
      setTimeout(() => {
        isSuccessfulNavigationRef.current = false;
      }, 100);
    }
  };

  // Handle modal cancel - stay on page and reset blocker
  const handleCancelExit = () => {
    setActiveModal(null);
    setPendingNavigation(null);
    nextLocationRef.current = null;
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleGoToBudgetOverview = () => {
    setShowInvoiceSuccessModal(false);
    // Set flag to bypass blocker for successful navigation
    isSuccessfulNavigationRef.current = true;
    // Defer navigation so the modal can unmount first. Navigating immediately can leave the
    // modal's full-screen overlay in the DOM or cause it to block clicks on the new page.
    requestAnimationFrame(() => {
      navigate(`${PAGE.BUDGETS.path}/${getValues('budget_id')}`);
      setTimeout(() => {
        isSuccessfulNavigationRef.current = false;
      }, 100);
    });
  };

  return (
    <>
      <MobileAppBar
        title="Invoice Creation"
        onRightIconClick={() => {}}
        rightIcon={<InfoIcon width={24} height={24} />}
      />

      <div className="flex-1 flex flex-col xl:flex-row items-stretch justify-between gap-6 w-full max-w-screen-xl mx-auto">
        {/* LEFT SIDEBAR */}
        <div className="hidden md:block xl:flex-1 xl:max-w-[224px] xl:sticky xl:top-0 space-y-6">
          <CreateInvoiceSteps activeStep={activeStep} className="w-full flex-row xl:flex-col" />

          {activeStep === 2 && (
            <InvoiceUploadOverview
              invoiceImg={uploadedFiles[0]}
              invoiceCompanyName={watch('invoicing_company_name')}
              invoiceName={watch('name')}
              invoiceCategory={watch('invoice_category')}
              paymentCurrency={watch('payment_currency')}
              paymentAmount={watch('payment_amount')}
              paymentType={watch('payment_type')}
            />
          )}

          {/* <div className="card flex flex-col items-center gap-4 py-5 px-6">
            <span className="w-[108px] h-[108px] border rounded-[16px] bg-lightPurple flex items-center justify-center">
              {uploadedFiles[0] ? (
                <img
                  src={typeof uploadedFiles[0] === 'string' ? uploadedFiles[0] : URL.createObjectURL(uploadedFiles[0])}
                  alt="Uploaded file"
                  className="w-full h-full object-cover rounded-[16px]"
                />
              ) : (
                <FileText className="w-16 h-16 object-cover rounded-[16px] text-lightPurple-200/80" />
              )}
            </span>

            <div className="space-y-1 flex flex-col items-center">
              <p className="text-sm text-secondary leading-[150%]">{watch('invoicing_company_name') || '-'}</p>
              <p className="text-base font-semibold leading-[100%]">{watch('name') || '-'}</p>
              <p className="text-sm text-secondary leading-[150%]">
                {watch('invoice_category') === 'expense' ? 'Expense' : 'Revenue'}{' '}
                <span className="text-primary font-bold"> &middot;</span>{' '}
                {watch('payment_currency') === 'EUR' ? '€' : '$'}
                {formatNumberToK(watch('payment_amount') || '0')}
              </p>
            </div>
            <p className="text-base flex items-center gap-1 font-medium text-secondary leading-[100%]">
              <span>
                {watch('payment_type') === 'ALREADY_PAID' ? (
                  <CompleteIcon />
                ) : (
                  <CircleXIcon className="w-[16px] h-[16px]" />
                )}
              </span>
              <span>{watch('payment_type') === 'ALREADY_PAID' ? 'Paid' : 'Not Paid'}</span>
            </p>
          </div> */}
        </div>

        {/* RIGHT MAIN CONTENT */}
        <div className="flex-1 flex flex-col gap-6 p-4 md:card !pb-8">
          <CreateInvoiceHeader title={from === 'invoiceDetail' ? 'Edit your Invoice' : 'Create your Invoice'} />

          {/* Mobile Steps - Only visible on mobile */}
          <div className="md:hidden">
            <CreateInvoiceStepsMobile activeStep={activeStep} />
          </div>

          <form className="flex-1" onSubmit={handleSubmit(onSubmit)}>
            {activeStep === 1 && (
              <CreateInvoiceUpload
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                uploadedFiles={uploadedFiles}
                onFilesChange={handleFilesChange}
                budgetOptions={budgetOptions}
                existingImageUrls={existingImageUrls}
                onRemoveExistingImage={handleRemoveExistingImage}
                maxAmount={maxAmount}
                isAmountDisabled={isAmountDisabled}
                invoiceCategory={invoiceCategory}
                paymentTermType={paymentTermType}
                setPaymentTermType={setPaymentTermType}
              />
            )}
            {activeStep === 2 && (
              <CreateInvoiceAllocation
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                control={control}
                budgetId={budgetId || budgetIdForm}
                invoiceCategoriesData={invoiceCategoriesData}
                bankAccountsData={bankAccounts}
                accountingCentersData={accountingCenters}
              />
            )}
            {activeStep === 3 && (
              <CreateInvoiceTags
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                initialTags={getValues('tags')}
              />
            )}
            {/* TODO: Add other steps here */}
          </form>

          <CreateInvoiceFooter
            activeStep={activeStep}
            onPrevClick={handlePrevClick}
            onNextClick={handleNextClick}
            loading={processingInvoice}
            isDraft={from === 'draft' && invoiceId !== null}
            onSaveDraft={handleCreateDraftInvoice}
          />
        </div>
      </div>

      {activeModal === 'exitInvoiceCreation' && (
        <InfoModal
          isOpen={activeModal === 'exitInvoiceCreation'}
          variant="confirm"
          title="Are you sure want to exit Invoice creation?"
          message={
            from === 'draft'
              ? 'The draft/invoice data will be saved.'
              : 'This invoice will be saved and you can edit it again in the draft section.'
          }
          buttonText="Leave"
          onButtonClick={handleExitInvoiceCreation}
          icon={<ExitOutlineIcon />}
          onClose={handleCancelExit}
        />
      )}

      {budgetOptions.length === 0 && !loadingBudgets && (
        <InfoModal
          isOpen={budgetOptions.length === 0 && !loadingBudgets}
          title="Oops, you don’t have any budget yet!"
          message="Please create a new budget to continue creating invoice."
          buttonText="Create Budget"
          onButtonClick={() => {
            navigate(PAGE.BUDGETS.path);
          }}
          icon={<BudgetEmptyIcon />}
          onClose={() => {}}
        />
      )}

      {showInvoiceSuccessModal && (
        <InfoModal
          isOpen={showInvoiceSuccessModal}
          // variant="confirm"
          title={from === 'invoiceDetail' ? 'Invoice Updated' : 'Invoice Created'}
          message={
            from === 'invoiceDetail'
              ? 'You have successfully updated the Invoice!'
              : 'Invoice already billed to selected Bank Account and MAE'
          }
          buttonText="Go to Budget Overview"
          onButtonClick={handleGoToBudgetOverview}
          icon={<SuccessIcon />}
          hideClose={true}
        />
      )}
    </>
  );
};

export default InvoiceCreatePage;
